import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  ListChecks,
  Pause,
  Play,
  RotateCcw,
  Save,
  Send,
  SlidersHorizontal,
  Trash2,
  Volume2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin, { type Region as WsRegion } from "wavesurfer.js/dist/plugins/regions.js";
import SpectrogramPlugin from "wavesurfer.js/dist/plugins/spectrogram.js";
import TimelinePlugin from "wavesurfer.js/dist/plugins/timeline.js";

import {
  MANUAL_DEFAULT,
  MANUAL_MAX,
  MANUAL_MIN,
  analyzeChannels,
  clampManualBoost,
  computeAutoGain,
  loadManualBoost,
  saveManualBoost,
} from "./audioGain";
import { PageHeader } from "@/components/PageHeader";
import { ProjectsApi, S3Api, TaggingsApi, TasksApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/lib/useIsMobile";
import type { Region as ApiRegion, TagDefinition } from "@/lib/types";
import { cn } from "@/lib/utils";

// Stable per-user palette for the "others' taggings" overlay. Distinct enough
// from the typical project tag colors that overlays don't visually fuse with
// the user's own editable regions.
const OVERLAY_COLORS = ["#f59e0b", "#ec4899", "#8b5cf6", "#10b981", "#ef4444", "#06b6d4"];
const overlayColorFor = (userId: number) => OVERLAY_COLORS[userId % OVERLAY_COLORS.length];

interface DraftRegion {
  // local id (we don't trust API ids after mutations)
  uid: string;
  start: number;
  end: number;
  label: string;
}

const newUid = () => Math.random().toString(36).slice(2, 10);

// Spectrogram dynamic range, persisted per-user in localStorage. Default
// matches the wavesurfer SpectrogramPlugin default so users who never touch
// the slider see the same image as before.
const SPEC_SETTINGS_KEY = "dronetag-spectrogram-settings";
const SPEC_RANGE_DEFAULT = 80;
const SPEC_RANGE_MIN = 40;
const SPEC_RANGE_MAX = 120;

// Vertical (frequency) axis scale. Linear is the default so existing users see
// the same image; mel spaces the axis perceptually (more resolution in the low
// frequencies where drone energy concentrates).
type SpecScale = "linear" | "mel";
const SPEC_SCALE_DEFAULT: SpecScale = "linear";

// Split the waveform area's CONTENT-BOX height between the waveform and the
// spectrogram (the timeline takes a thin strip). `available` must be the inner
// waveform div's clientHeight (the card's content box, excluding card padding) —
// not the card's clientHeight, which includes padding and would overshoot.
const SPECTRO_TIMELINE_H = 18;
function computeSpectroHeights(available: number): { waveformH: number; spectrogramH: number } {
  // Fit the waveform + timeline + spectrogram WITHIN `available` so nothing gets
  // clipped on short viewports (e.g. iPhone SE). The spectrogram takes whatever
  // is left after a (clamped) waveform slice — it shrinks rather than overflow.
  // Hard floor: below ~106px (40 + 48 + 18) the floors pin and the bottom clips;
  // portrait phones are well above that.
  const usable = Math.max(0, available - SPECTRO_TIMELINE_H);
  const waveformH = Math.max(40, Math.min(120, Math.round(usable * 0.26)));
  const spectrogramH = Math.max(48, usable - waveformH);
  return { waveformH, spectrogramH };
}

function loadSpectrogramSettings(): { rangeDB: number; scale: SpecScale } {
  try {
    const raw = localStorage.getItem(SPEC_SETTINGS_KEY);
    if (!raw) return { rangeDB: SPEC_RANGE_DEFAULT, scale: SPEC_SCALE_DEFAULT };
    const parsed = JSON.parse(raw) as { rangeDB?: unknown; scale?: unknown };
    const rangeDB =
      typeof parsed.rangeDB === "number" && Number.isFinite(parsed.rangeDB)
        ? Math.min(SPEC_RANGE_MAX, Math.max(SPEC_RANGE_MIN, parsed.rangeDB))
        : SPEC_RANGE_DEFAULT;
    const scale: SpecScale = parsed.scale === "mel" ? "mel" : SPEC_SCALE_DEFAULT;
    return { rangeDB, scale };
  } catch {
    return { rangeDB: SPEC_RANGE_DEFAULT, scale: SPEC_SCALE_DEFAULT };
  }
}

export function TaggingPage() {
  const { projectId, taskId } = useParams<{ projectId: string; taskId: string }>();
  const pid = Number(projectId);
  const tid = Number(taskId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  // --- data ---
  const project = useQuery({
    queryKey: ["project", pid],
    queryFn: () => ProjectsApi.get(pid),
    enabled: Number.isFinite(pid),
  });
  const task = useQuery({
    queryKey: ["task", tid],
    queryFn: () => TasksApi.get(tid),
    enabled: Number.isFinite(tid),
  });
  const presign = useQuery({
    queryKey: ["presign", tid],
    queryFn: () => S3Api.presignAudio(tid),
    enabled: Number.isFinite(tid),
    staleTime: 0,
  });
  const tagging = useQuery({
    queryKey: ["tagging", tid],
    queryFn: () => TaggingsApi.forTask(tid),
    enabled: Number.isFinite(tid),
  });

  // Owner/superuser-only: other reviewers' submitted taggings, for the overlay.
  // The query is only enabled once we know whether the current user qualifies,
  // so non-owners never hit the endpoint (which would 403 anyway).
  const currentUser = useAuth((s) => s.user);
  const isOwnerOrAdmin = Boolean(
    currentUser && project.data && (currentUser.is_superuser || currentUser.id === project.data.owner_id),
  );
  const otherTaggings = useQuery({
    queryKey: ["otherTaggings", tid],
    queryFn: () => TaggingsApi.othersForTask(tid),
    enabled: Number.isFinite(tid) && isOwnerOrAdmin,
  });
  const [showOthers, setShowOthers] = useState(false);

  // --- local region state (the source of truth while editing) ---
  const [regions, setRegions] = useState<DraftRegion[]>([]);
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pxPerSec, setPxPerSec] = useState(50);
  const [playing, setPlaying] = useState(false);
  const [regionsPanelOpen, setRegionsPanelOpen] = useState(false);
  // In-session stack of task ids visited via Next / Submit, so the Back button
  // returns to the previously-viewed file. This component stays mounted while
  // navigating between tasks (only the :taskId param changes), so the stack
  // persists; it resets when you leave the tagging view (e.g. to the task list).
  // Empty = we're on the first file opened this session → Back is disabled.
  const [taskHistory, setTaskHistory] = useState<number[]>([]);

  // --- audio gain (client-side loudness boost; never modifies the S3 audio) ---
  // `autoGain` is derived from the decoded buffer's loudness so quiet drone
  // recordings land near a consistent level; `manualBoost` is a user multiplier
  // on top, persisted per-browser. Effective gain = autoGain * manualBoost.
  const [autoGain, setAutoGain] = useState(1);
  const [manualBoost, setManualBoost] = useState<number>(loadManualBoost);
  // Mobile only: the spectrogram/volume sliders are collapsed by default so the
  // waveform + region controls fit the screen without scrolling. Always shown on
  // desktop (md:flex), regardless of this flag.
  const [mobileControlsOpen, setMobileControlsOpen] = useState(false);

  // Spectrogram dynamic range. `rangeDB` tracks the live slider position;
  // `committedRangeDB` is what wavesurfer reads — it updates on a debounce so
  // dragging the slider doesn't redraw on every pixel.
  const initialSpec = useMemo(loadSpectrogramSettings, []);
  const [rangeDB, setRangeDB] = useState(initialSpec.rangeDB);
  const [committedRangeDB, setCommittedRangeDB] = useState(initialSpec.rangeDB);
  // Frequency-axis scale (linear | mel). Changing it recomputes the spectrogram
  // FFT in place (off the main thread); not debounced.
  const [specScale, setSpecScale] = useState<SpecScale>(initialSpec.scale);
  useEffect(() => {
    const t = window.setTimeout(() => {
      setCommittedRangeDB(rangeDB);
      try {
        localStorage.setItem(SPEC_SETTINGS_KEY, JSON.stringify({ rangeDB, scale: specScale }));
      } catch {
        // ignore quota / privacy-mode errors
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [rangeDB, specScale]);

  // Sync server regions → local once the tagging loads
  useEffect(() => {
    if (tagging.data) {
      setRegions(
        tagging.data.regions.map((r): DraftRegion => ({
          uid: newUid(),
          start: r.start_seconds,
          end: r.end_seconds,
          label: r.label,
        })),
      );
      setDirty(false);
    }
  }, [tagging.data]);

  // Default active label = first project tag
  useEffect(() => {
    if (!activeLabel && project.data?.tag_schema?.length) {
      setActiveLabel(project.data.tag_schema[0].name);
    }
  }, [project.data, activeLabel]);

  const tagByName = useMemo(() => {
    const m = new Map<string, TagDefinition>();
    project.data?.tag_schema?.forEach((t) => m.set(t.name, t));
    return m;
  }, [project.data]);

  const colorFor = useCallback(
    (label: string) => tagByName.get(label)?.color ?? "#22d3ee",
    [tagByName],
  );

  // --- wavesurfer ---
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const regionsPluginRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null);
  const spectrogramPluginRef = useRef<ReturnType<typeof SpectrogramPlugin.create> | null>(null);
  // Cache the computed FFT frequencies per scale (linear/mel) for the current
  // file, so toggling the scale a second time swaps them in instantly instead of
  // recomputing. Cleared whenever the player rebuilds (the audio buffer changes).
  const freqCacheRef = useRef<Map<SpecScale, unknown>>(new Map());
  // Map wavesurfer-region id → our local uid so we can correlate without races
  const regionIdMap = useRef<Map<string, string>>(new Map());
  // Set of wavesurfer-region IDs that belong to the "others' taggings" overlay.
  // Used by event handlers to ignore these regions, and by the redraw function
  // to clean up only its own regions without touching the user's draft.
  const overlayRegionIdsRef = useRef<Set<string>>(new Set());
  // Set true while restoring server regions into wavesurfer so the region-* event
  // handlers don't double-append into our local state.
  const isRestoringRef = useRef(false);
  // Track which tagging row we've already restored — prevents the sync effect from
  // re-running on every re-render when TanStack Query returns a new object ref.
  const restoredTaggingIdRef = useRef<number | null>(null);
  // Z-key range selection: first Z press records the start time, second creates the region.
  const selectionStartRef = useRef<number | null>(null);
  const [selectionStartTime, setSelectionStartTime] = useState<number | null>(null);
  const [wsError, setWsError] = useState<string | null>(null);
  const [wsReady, setWsReady] = useState(false);

  // Web Audio gain chain: <audio> → MediaElementSource → GainNode → limiter →
  // destination. The AudioContext is created once and reused across task
  // switches (browsers cap how many can exist); the source/gain/limiter nodes
  // are rebuilt per task because WaveSurfer creates a fresh media element.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const mediaSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const limiterRef = useRef<DynamicsCompressorNode | null>(null);
  // Carry playhead + play state across a player REBUILD of the same file (e.g.
  // the mobile settings toggle resizes the waveform card, so we rebuild to re-fit
  // the spectrogram height). Not restored across a task switch (different URL).
  const restoreTimeRef = useRef<number | null>(null);
  const restorePlayingRef = useRef(false);
  const effectUrlRef = useRef<string | null>(null);
  // Latest manual boost, read inside the wavesurfer effect (which only re-runs
  // on URL change) so a freshly created GainNode starts at the persisted level.
  const manualBoostRef = useRef(manualBoost);
  useEffect(() => {
    manualBoostRef.current = manualBoost;
  }, [manualBoost]);

  useEffect(() => {
    if (!presign.data?.url) return;
    if (!waveformRef.current) return;

    setWsError(null);
    setWsReady(false);
    // New file → recompute auto-gain from scratch (manual boost is preserved).
    setAutoGain(1);
    // New player/audio buffer → the cached per-scale FFTs no longer apply.
    freqCacheRef.current.clear();
    // NOTE: we intentionally do NOT reset restoredTaggingIdRef here. The region
    // restore effect uses it to tell a task switch (restore from server) apart
    // from a same-file rebuild (restore from local state, preserving unsaved edits).

    const regionsPlugin = RegionsPlugin.create();
    regionsPluginRef.current = regionsPlugin;

    let spectrogramPlugin: ReturnType<typeof SpectrogramPlugin.create>;

    // Allocate the available vertical space inside the card between waveform +
    // spectrogram (plus a small timeline). Drone work cares more about the
    // spectrogram than the waveform, so give it the lion's share.
    // The waveform card has a bounded height on every breakpoint (the page never
    // scrolls), so size from the inner div's measured height (= the card's
    // content box; it's h-full, so card padding is correctly excluded).
    const available = waveformRef.current.clientHeight || 320;
    const { waveformH: WAVEFORM_H, spectrogramH: SPECTROGRAM_H } = computeSpectroHeights(available);

    // Own the <audio> element so we can mark it CORS-clean. Routing audio through
    // a Web Audio MediaElementSource (for the gain boost) outputs SILENCE for
    // cross-origin media unless the element loaded with CORS. The presigned S3
    // URL is cross-origin and the bucket allows CORS, so `crossOrigin` here keeps
    // the gain chain audible. Must be set before the src (WaveSurfer sets src).
    const mediaEl = document.createElement("audio");
    mediaEl.crossOrigin = "anonymous";

    let ws: WaveSurfer;
    try {
      ws = WaveSurfer.create({
        container: waveformRef.current,
        media: mediaEl,
        url: presign.data.url,
        waveColor: "#94a3b8",        // slate-400 — visible on dark surface
        progressColor: "#22d3ee",    // cyan-400 (played portion)
        cursorColor: "#f59e0b",      // amber — visible play cursor
        cursorWidth: 2,
        barWidth: 2,
        barGap: 1,
        barRadius: 2,
        height: WAVEFORM_H,
        minPxPerSec: pxPerSec,
        autoCenter: true,
        mediaControls: false,
        normalize: true,             // scale peaks to fill the canvas — drone audio is often quiet
        plugins: [
          regionsPlugin,
          (spectrogramPlugin = SpectrogramPlugin.create({
            // No `container` — let the plugin append inside wavesurfer's own container
            // so the wavesurfer cursor element overlays both waveform and spectrogram.
            // labels are disabled because they render ON TOP of the leftmost spectrogram
            // pixels and break visual time-alignment with the waveform.
            labels: false,
            height: SPECTROGRAM_H,
            fftSamples: 1024,
            // freqMax = the plugin's computed Nyquist (4000) for both scales, so
            // there's no white pre-fill (the old v7.8 bug) and no canvas hack is
            // needed. Shows the real 0–4 kHz range; the scale toggle only changes
            // how that range is spaced vertically.
            frequencyMax: 4000,
            scale: specScale,
            rangeDB: committedRangeDB,
            // Run the FFT in a Web Worker so recomputes (initial load, scale
            // change) don't block the main thread / freeze the UI. Falls back to
            // the main thread automatically if the worker can't be created.
            useWebWorker: true,
          })),
          TimelinePlugin.create({
            height: SPECTRO_TIMELINE_H,
            insertPosition: "beforebegin",
            style: { color: "#9ca3af", fontSize: "10px" },
          }),
        ],
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[wavesurfer] create failed:", e);
      setWsError(`Wavesurfer init failed: ${msg}`);
      return;
    }

    wsRef.current = ws;
    spectrogramPluginRef.current = spectrogramPlugin;

    // Build the Web Audio gain chain on this task's media element. We tap the
    // <audio> element with a MediaElementSource and route it through a GainNode
    // (for >1 boost, which setVolume can't do) and a limiter that catches peaks
    // so over-boosting doesn't clip harshly. Wrapped in try/catch because
    // getMediaElement / createMediaElementSource can throw on some backends.
    try {
      {
        let ctx = audioCtxRef.current;
        if (!ctx) {
          const Ctor =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext;
          if (Ctor) {
            ctx = new Ctor();
            audioCtxRef.current = ctx;
          }
        }
        if (ctx) {
          const source = ctx.createMediaElementSource(mediaEl);
          const gain = ctx.createGain();
          // Auto-gain isn't known until the buffer decodes, so start at a
          // neutral 1× (× the persisted manual boost). The decode handler +
          // apply-effect ramp to the real level once loudness is measured.
          gain.gain.value = 1 * manualBoostRef.current;
          const limiter = ctx.createDynamicsCompressor();
          limiter.threshold.value = -1; // dBFS — start limiting just below full scale
          limiter.knee.value = 0;
          limiter.ratio.value = 20; // hard-ish limiting
          limiter.attack.value = 0.003;
          limiter.release.value = 0.25;
          source.connect(gain);
          gain.connect(limiter);
          limiter.connect(ctx.destination);
          mediaSourceRef.current = source;
          gainNodeRef.current = gain;
          limiterRef.current = limiter;
        }
      }
    } catch (e) {
      // Non-fatal: audio still plays at raw volume if the gain chain fails.
      console.warn("[audio-gain] failed to set up gain chain:", e);
    }

    ws.on("error", (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      setWsError(`Audio load/decode failed: ${msg}`);
    });
    ws.on("ready", () => {
      setWsReady(true);
      // If this is a rebuild of the SAME file (breakpoint cross, not a task
      // switch), restore the playhead + playback so the rebuild is seamless.
      if (effectUrlRef.current === presign.data?.url && restoreTimeRef.current != null) {
        const t = restoreTimeRef.current;
        restoreTimeRef.current = null;
        ws.setTime(t);
        if (restorePlayingRef.current) void ws.play();
      }
    });

    // After every spectrogram draw, stash the freshly-computed FFT keyed by the
    // current scale so switching back to it later is an instant cache swap (no
    // recompute). 'ready' fires on each draw, including fastRender.
    spectrogramPlugin.on("ready", () => {
      const p = spectrogramPlugin as unknown as { scale?: SpecScale; cachedFrequencies?: unknown };
      if (p.cachedFrequencies && (p.scale === "linear" || p.scale === "mel")) {
        freqCacheRef.current.set(p.scale, p.cachedFrequencies);
      }
    });
    ws.on("play", () => {
      setPlaying(true);
      // AudioContext starts suspended under the autoplay policy; the play click
      // is a user gesture, so it's safe to resume here.
      void audioCtxRef.current?.resume();
    });
    ws.on("pause", () => setPlaying(false));
    ws.on("finish", () => setPlaying(false));

    // Drag to create a region — uses current active label.
    // Desktop only: on touch, drag-selection would consume the gesture and block
    // panning a zoomed waveform, so on mobile we leave drag free for native
    // scroll/pan and create regions via the "Set region start/end" buttons.
    ws.on("decode", () => {
      if (!isMobile) {
        regionsPlugin.enableDragSelection({ color: "rgba(34, 211, 238, 0.15)" });
      }
      // Derive auto-gain from the decoded buffer's loudness so quiet files are
      // boosted toward a consistent level.
      try {
        const buf = ws.getDecodedData();
        if (buf) {
          const channels: Float32Array[] = [];
          for (let c = 0; c < buf.numberOfChannels; c++) {
            channels.push(buf.getChannelData(c));
          }
          const { rms, peak } = analyzeChannels(channels);
          setAutoGain(computeAutoGain(rms, peak));
        }
      } catch (e) {
        console.warn("[audio-gain] failed to analyze buffer:", e);
      }
    });

    regionsPlugin.on("region-created", (region: WsRegion) => {
      // Programmatic adds (server-region restore) set isRestoring=true so we don't
      // double-append to state. The sync effect below handles the local-state side.
      if (isRestoringRef.current) return;
      if (overlayRegionIdsRef.current.has(region.id)) return;
      const label = activeLabelRef.current ?? project.data?.tag_schema?.[0]?.name ?? "Drone";
      const color = tagByNameRef.current.get(label)?.color ?? "#22d3ee";
      region.setOptions({
        color: hexToRgba(color, 0.35),
        content: buildRegionContent(label, () => region.remove()),
      });
      const uid = newUid();
      regionIdMap.current.set(region.id, uid);
      const draft: DraftRegion = { uid, start: region.start, end: region.end, label };
      setRegions((prev) => [...prev, draft]);
      setDirty(true);
    });

    regionsPlugin.on("region-updated", (region: WsRegion) => {
      if (isRestoringRef.current) return;
      if (overlayRegionIdsRef.current.has(region.id)) return;
      const uid = regionIdMap.current.get(region.id);
      if (!uid) return;
      setRegions((prev) =>
        prev.map((r) => (r.uid === uid ? { ...r, start: region.start, end: region.end } : r)),
      );
      setDirty(true);
    });

    regionsPlugin.on("region-removed", (region: WsRegion) => {
      if (isRestoringRef.current) return;
      if (overlayRegionIdsRef.current.has(region.id)) {
        overlayRegionIdsRef.current.delete(region.id);
        return;
      }
      const uid = regionIdMap.current.get(region.id);
      if (!uid) return;
      regionIdMap.current.delete(region.id);
      setRegions((prev) => prev.filter((r) => r.uid !== uid));
      setDirty(true);
    });


    return () => {
      // Remember the playhead so a same-file rebuild (breakpoint cross) restores it.
      try {
        restoreTimeRef.current = ws.getCurrentTime();
        restorePlayingRef.current = ws.isPlaying();
        effectUrlRef.current = presign.data?.url ?? null;
      } catch {
        // ignore
      }
      // Tear down the gain chain (the AudioContext itself is kept for reuse).
      // ws.destroy() removes the media element, so the source node is dead;
      // disconnect explicitly to be safe and allow GC.
      try {
        mediaSourceRef.current?.disconnect();
        gainNodeRef.current?.disconnect();
        limiterRef.current?.disconnect();
      } catch {
        // ignore — nodes may already be detached
      }
      mediaSourceRef.current = null;
      gainNodeRef.current = null;
      limiterRef.current = null;
      ws.destroy();
      wsRef.current = null;
      regionsPluginRef.current = null;
      spectrogramPluginRef.current = null;
      regionIdMap.current.clear();
    };
    // Only a URL change (task switch) or breakpoint cross rebuilds the player.
    // Scale, dB range, and the settings-panel resize are applied LIVE on the
    // existing spectrogram plugin (see the effect below) — no costly re-decode.
    // Playhead is restored for same-file rebuilds. Teardown is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presign.data?.url, isMobile]);

  // Apply the effective gain (autoGain * manualBoost) whenever either changes.
  // setTargetAtTime ramps smoothly to avoid clicks when the slider moves.
  useEffect(() => {
    const gain = gainNodeRef.current;
    const ctx = audioCtxRef.current;
    if (!gain || !ctx) return;
    gain.gain.setTargetAtTime(autoGain * manualBoost, ctx.currentTime, 0.02);
  }, [autoGain, manualBoost]);

  // Persist the manual boost per-browser.
  useEffect(() => {
    saveManualBoost(manualBoost);
  }, [manualBoost]);

  // Release the shared AudioContext when the page unmounts.
  useEffect(() => {
    return () => {
      void audioCtxRef.current?.close();
      audioCtxRef.current = null;
    };
  }, []);

  // Apply display settings to the existing spectrogram plugin IN PLACE — never
  // destroy/recreate the wavesurfer instance, which would re-decode the audio
  // (~1s stall), reset the playhead, and drop regions.
  //
  // The raw FFT is cached in `cachedFrequencies`. `rangeDB` and `height` are
  // applied purely at DRAW time, so changing them only needs `fastRender()`
  // (redraw from cache — instant). But the `scale` (mel) filter is baked INTO
  // the cached FFT, so a scale change must recompute it: clearCache() + render().
  // That recompute is ~1s, but useWebWorker runs it off the main thread, so the
  // UI stays responsive and the old image shows until the new one is ready.
  //
  // The mobile settings panel toggles `mobileControlsOpen`, which resizes the
  // waveform card; we re-fit the waveform + spectrogram heights here too.

  // Measure the inner waveform div (= the card's content box) and apply the split
  // to the live player. Shared by the settings effect and the ResizeObserver so
  // the sizing protocol has exactly one implementation. setOptions({height})
  // re-renders the waveform from existing peaks — no decode.
  const applySpectroHeights = useCallback((): { waveformChanged: boolean; spectrogramChanged: boolean } => {
    const ws = wsRef.current;
    const plugin = spectrogramPluginRef.current;
    const inner = waveformRef.current;
    if (!ws || !plugin || !inner) return { waveformChanged: false, spectrogramChanged: false };
    const p = plugin as unknown as { height: number };
    const { waveformH, spectrogramH } = computeSpectroHeights(inner.clientHeight);
    const waveformChanged = ws.options.height !== waveformH;
    if (waveformChanged) ws.setOptions({ height: waveformH });
    const spectrogramChanged = p.height !== spectrogramH;
    if (spectrogramChanged) p.height = spectrogramH;
    return { waveformChanged, spectrogramChanged };
  }, []);

  const prevScaleRef = useRef(specScale);
  useEffect(() => {
    const ws = wsRef.current;
    const plugin = spectrogramPluginRef.current;
    if (!ws || !plugin || !wsReady) return;
    const p = plugin as unknown as {
      rangeDB: number;
      scale: SpecScale;
      height: number;
      cachedFrequencies?: unknown;
      cachedResampledData?: unknown;
      cachedWidth?: number;
      fastRender?: () => void;
      render?: () => void;
      clearCache?: () => void;
    };

    const scaleChanged = prevScaleRef.current !== specScale;
    prevScaleRef.current = specScale;

    // Re-fit heights to the card's current size (it changes when the mobile
    // settings panel opens/closes).
    applySpectroHeights();

    p.rangeDB = committedRangeDB;
    p.scale = specScale;

    if (scaleChanged) {
      const cached = freqCacheRef.current.get(specScale);
      if (cached && typeof p.fastRender === "function") {
        // We've already computed this scale for this file — swap the FFT back in
        // and redraw instantly, no recompute. Invalidate the width-resample cache
        // (keyed only on width) so it re-derives from the swapped FFT.
        p.cachedFrequencies = cached;
        p.cachedResampledData = null;
        p.cachedWidth = 0;
        p.fastRender();
      } else {
        // First time on this scale: the mel filter is baked into the FFT, so
        // recompute it (off the main thread via the worker). The 'ready' handler
        // caches the result so the next switch is instant.
        p.clearCache?.();
        p.render?.();
      }
    } else if (p.cachedFrequencies && typeof p.fastRender === "function") {
      // No re-FFT needed — redraw from cache instantly.
      p.fastRender();
    }
    // First paint (no cache yet, scale unchanged): the plugin's own initial
    // render reads these fields, so we don't trigger an extra FFT pass here.
  }, [committedRangeDB, specScale, mobileControlsOpen, wsReady, pxPerSec, applySpectroHeights]);

  // Re-fit waveform + spectrogram heights to the card's ACTUAL height whenever it
  // changes. The card is flex-1, so it shrinks when the header reflows (the async
  // "N taggings" badge / subtitle load after WaveSurfer was created) or on
  // rotation / viewport change — without this, the spectrogram keeps its
  // creation-time height and gets clipped by the card's overflow-hidden (it
  // disappeared entirely on short screens like the iPhone SE). The card's height
  // is set by the flex layout, not its content, so resizing the canvas here can't
  // feed back into another resize.
  // Relies on the inner waveform div being unconditionally mounted for the
  // component's whole life (see the no-early-return note above the render) —
  // if that ever changes, switch this to a callback ref.
  useEffect(() => {
    const inner = waveformRef.current;
    if (!inner || typeof ResizeObserver === "undefined") return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const plugin = spectrogramPluginRef.current;
        if (!plugin) return;
        const p = plugin as unknown as {
          cachedFrequencies?: unknown;
          fastRender?: () => void;
        };
        const { waveformChanged, spectrogramChanged } = applySpectroHeights();
        // When the waveform height changed, setOptions already emits 'redraw',
        // which the plugin answers with its own throttled fastRender — so an
        // explicit redraw here would just repaint twice. Only trigger it when
        // ONLY the spectrogram height changed (which emits no event).
        if (
          spectrogramChanged &&
          !waveformChanged &&
          p.cachedFrequencies &&
          typeof p.fastRender === "function"
        ) {
          p.fastRender();
        }
      });
    });
    ro.observe(inner);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [applySpectroHeights]);

  // Refs that the wavesurfer event handlers can read latest values from
  const activeLabelRef = useRef<string | null>(activeLabel);
  const regionsRef = useRef(regions);
  const tagByNameRef = useRef(tagByName);
  useEffect(() => {
    activeLabelRef.current = activeLabel;
  }, [activeLabel]);
  useEffect(() => {
    regionsRef.current = regions;
  }, [regions]);
  useEffect(() => {
    tagByNameRef.current = tagByName;
  }, [tagByName]);

  // Draw (or re-draw) the read-only overlay regions for other reviewers' submitted
  // taggings. Idempotent: clears its previous overlay regions first, then re-adds
  // based on current state. Called from the dedicated effect below AND after user-
  // region restores (which wipe ALL regions, including overlays).
  const drawOverlays = useCallback(() => {
    const plugin = regionsPluginRef.current;
    if (!plugin) return;
    // Remove only overlay regions (leave user's draft alone). `region-removed`
    // events will fire and clean up `overlayRegionIdsRef` entries.
    isRestoringRef.current = true;
    try {
      const ids = new Set(overlayRegionIdsRef.current);
      plugin.getRegions().forEach((r) => {
        if (ids.has(r.id)) r.remove();
      });
      overlayRegionIdsRef.current.clear();
      if (!showOthers || !otherTaggings.data) return;
      for (const t of otherTaggings.data) {
        const color = overlayColorFor(t.user_id);
        const who = t.display_name?.trim() || t.username;
        for (const r of t.regions) {
          const wsr = plugin.addRegion({
            start: r.start_seconds,
            end: r.end_seconds,
            color: hexToRgba(color, 0.18),
            drag: false,
            resize: false,
          });
          wsr.setOptions({ content: buildOverlayRegionContent(who, r.label, color) });
          overlayRegionIdsRef.current.add(wsr.id);
        }
      }
    } finally {
      isRestoringRef.current = false;
    }
  }, [showOthers, otherTaggings.data]);

  useEffect(() => {
    if (!wsReady) return;
    drawOverlays();
  }, [wsReady, drawOverlays]);

  // Sync server-loaded regions INTO wavesurfer once. Keyed by tagging.id so the
  // effect doesn't re-run on every TanStack Query refetch (which produces a new
  // object reference even when the data hasn't changed).
  const taggingId = tagging.data?.id ?? null;
  useEffect(() => {
    if (!wsReady || !taggingId || !tagging.data || !regionsPluginRef.current) return;

    // First restore of this tagging (initial load / task switch) → seed wavesurfer
    // from SERVER regions. A later rebuild of the SAME tagging (e.g. the mobile
    // settings toggle resized the player) → restore from LOCAL draft state so
    // unsaved edits survive; align regionIdMap to the local uids.
    const firstTime = restoredTaggingIdRef.current !== taggingId;
    // regionsRef.current is current here: the only same-file rebuild is a
    // breakpoint cross, which issues no setRegions, so no update is in flight.
    const source: DraftRegion[] = firstTime
      ? tagging.data.regions.map((r) => ({
          uid: newUid(),
          start: r.start_seconds,
          end: r.end_seconds,
          label: r.label,
        }))
      : regionsRef.current;

    const plugin = regionsPluginRef.current;
    isRestoringRef.current = true;
    try {
      plugin.getRegions().forEach((r) => r.remove());
      regionIdMap.current.clear();
      for (const r of source) {
        const wsr = plugin.addRegion({
          start: r.start,
          end: r.end,
          color: hexToRgba(colorFor(r.label), 0.35),
          drag: true,
          resize: true,
        });
        wsr.setOptions({ content: buildRegionContent(r.label, () => wsr.remove()) });
        regionIdMap.current.set(wsr.id, r.uid);
      }
      restoredTaggingIdRef.current = taggingId;
    } finally {
      isRestoringRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsReady, taggingId]);

  // Apply zoom changes after mount. Guard on wsReady: zoom() throws
  // "No audio loaded" if called before the buffer decodes, which happens when
  // switching tasks quickly (a new instance exists but hasn't loaded yet).
  useEffect(() => {
    if (!wsReady) return;
    try {
      wsRef.current?.zoom(pxPerSec);
    } catch {
      // instance was torn down / not ready mid-switch — safe to ignore
    }
  }, [pxPerSec, wsReady]);

  // --- mutations ---
  const save = useMutation({
    mutationFn: async (submit: boolean) => {
      const payload: ApiRegion[] = regions.map((r) => ({
        start_seconds: Number(r.start.toFixed(3)),
        end_seconds: Number(r.end.toFixed(3)),
        label: r.label,
      }));
      return TaggingsApi.save(tid, payload, submit);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["tagging", tid] });
      qc.invalidateQueries({ queryKey: ["task", tid] });
      qc.invalidateQueries({ queryKey: ["tasks", pid] });
      setDirty(false);
      if (data.state === "submitted") {
        // After submit, hop to next task automatically — record the current task
        // so Back can return to it.
        TasksApi.next(pid)
          .then((next) => {
            if (next.id !== tid) {
              setTaskHistory((h) => [...h, tid]);
              navigate(`/projects/${pid}/tasks/${next.id}`);
            }
          })
          .catch(() => {});
      }
    },
  });

  // Mark a region boundary at the current playhead — first call records the
  // start, second call closes and creates the region. Shared by the Z key and
  // the mobile "Set start / Set end" buttons.
  const markRegionPoint = useCallback(() => {
    const ws = wsRef.current;
    const plugin = regionsPluginRef.current;
    if (!ws || !plugin) return;
    const t = ws.getCurrentTime();
    if (selectionStartRef.current === null) {
      selectionStartRef.current = t;
      setSelectionStartTime(t);
    } else {
      const start = selectionStartRef.current;
      selectionStartRef.current = null;
      setSelectionStartTime(null);
      const [s, end] = t >= start ? [start, t] : [t, start];
      if (end - s < 0.05) return; // ignore tiny / zero-length ranges
      const label = activeLabelRef.current ?? project.data?.tag_schema?.[0]?.name ?? "Drone";
      const color = tagByNameRef.current.get(label)?.color ?? "#22d3ee";
      plugin.addRegion({ start: s, end, color: hexToRgba(color, 0.35), content: label, drag: true, resize: true });
      // region-created handler will append to local state + set dirty
    }
  }, [project.data]);

  const cancelSelection = useCallback(() => {
    selectionStartRef.current = null;
    setSelectionStartTime(null);
  }, []);

  // Move the playhead by `delta` seconds (clamped). Shared by arrow keys and the
  // mobile nudge buttons.
  const nudgePlayhead = useCallback((delta: number) => {
    const ws = wsRef.current;
    if (!ws) return;
    ws.setTime(Math.max(0, Math.min(ws.getDuration(), ws.getCurrentTime() + delta)));
  }, []);

  // Adjust one edge of an existing region; routes through wavesurfer so the
  // region-updated handler keeps local state in sync. For mobile fine-tuning.
  const nudgeRegionBound = useCallback((uid: string, edge: "start" | "end", delta: number) => {
    const entry = [...regionIdMap.current.entries()].find(([, u]) => u === uid);
    if (!entry) return;
    const wsr = regionsPluginRef.current?.getRegions().find((r) => r.id === entry[0]);
    if (!wsr) return;
    const start = edge === "start" ? Math.max(0, wsr.start + delta) : wsr.start;
    const end = edge === "end" ? wsr.end + delta : wsr.end;
    if (end - start < 0.05) return;
    wsr.setOptions({ start, end });
  }, []);

  // Keyboard shortcuts (desktop)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === "Space") {
        e.preventDefault();
        wsRef.current?.playPause();
        return;
      }
      if (e.code === "KeyZ" || e.key.toLowerCase() === "z") {
        e.preventDefault();
        markRegionPoint();
        return;
      }
      // Esc — cancel an in-progress Z selection
      if (e.code === "Escape" && selectionStartRef.current !== null) {
        cancelSelection();
        return;
      }
      // ←/→ nudge the playhead. Shift = bigger step.
      if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
        e.preventDefault();
        const step = e.shiftKey ? 5 : 1;
        nudgePlayhead(e.code === "ArrowLeft" ? -step : step);
        return;
      }
      // X — cycle through tags
      if ((e.code === "KeyX" || e.key.toLowerCase() === "x") && project.data?.tag_schema?.length) {
        e.preventDefault();
        const tags = project.data.tag_schema;
        const currentIdx = tags.findIndex((t) => t.name === activeLabelRef.current);
        const nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % tags.length;
        setActiveLabel(tags[nextIdx].name);
        return;
      }
      if (project.data?.tag_schema) {
        for (const tag of project.data.tag_schema) {
          if (tag.shortcut && e.key.toLowerCase() === tag.shortcut.toLowerCase()) {
            setActiveLabel(tag.name);
          }
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [project.data, markRegionPoint, cancelSelection, nudgePlayhead]);

  const removeRegion = useCallback((uid: string) => {
    const entry = [...regionIdMap.current.entries()].find(([, u]) => u === uid);
    if (entry) {
      const wsr = regionsPluginRef.current?.getRegions().find((r) => r.id === entry[0]);
      wsr?.remove();
    } else {
      setRegions((prev) => prev.filter((r) => r.uid !== uid));
      setDirty(true);
    }
  }, []);

  const resetToServer = useCallback(() => {
    if (!tagging.data) return;
    setRegions(
      tagging.data.regions.map((r) => ({
        uid: newUid(),
        start: r.start_seconds,
        end: r.end_seconds,
        label: r.label,
      })),
    );
    setDirty(false);
    // Re-render wavesurfer regions from the freshly-reset state
    const plugin = regionsPluginRef.current;
    if (plugin && wsRef.current) {
      plugin.getRegions().forEach((r) => r.remove());
      regionIdMap.current.clear();
      tagging.data.regions.forEach((r) => {
        const wsr = plugin.addRegion({
          start: r.start_seconds,
          end: r.end_seconds,
          color: hexToRgba(colorFor(r.label), 0.35),
          drag: true,
          resize: true,
        });
        wsr.setOptions({ content: buildRegionContent(r.label, () => wsr.remove()) });
        regionIdMap.current.set(wsr.id, newUid());
      });
    }
  }, [tagging.data, colorFor]);

  // Note: do NOT early-return on task/project loading — the waveform/spectrogram
  // refs must be in the DOM on the very first render so the wavesurfer effect can
  // grab them. Render the layout immediately with empty placeholders.

  return (
    // Mobile: natural flow — the page scrolls (Layout's <main> is overflow-y-auto)
    // and the waveform card below has a FIXED height so the spectrogram is as big
    // as on large phones; annotators scroll to reach the controls underneath.
    // Desktop: unchanged (the inner container keeps its own h-[calc(100vh-93px)]).
    <div>
      <div className="shrink-0">
      <PageHeader
        title={task.data?.file_name ?? "Task"}
        subtitle={
          <>
            <span className="hidden font-mono text-xs md:inline">{task.data?.s3_key}</span>
            {task.data && (
              <span
                className={`ml-3 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
                  task.data.tagging_count === 0
                    ? "bg-status-pending/15 text-text-muted"
                    : task.data.tagging_count < 3
                      ? "bg-accent/15 text-accent"
                      : "bg-status-warn/15 text-status-warn"
                }`}
              >
                <span className="font-semibold">{task.data.tagging_count}</span>
                {task.data.tagging_count === 1 ? "tagging" : "taggings"} so far
              </span>
            )}
            {dirty && <span className="ml-2 text-status-warn">• unsaved changes</span>}
          </>
        }
        actions={
          <>
            <button
              className="btn-ghost"
              onClick={() => setRegionsPanelOpen((v) => !v)}
              title={regionsPanelOpen ? "Hide regions panel" : "Show regions panel"}
            >
              <ListChecks size={16} /> <span className="hidden sm:inline">Regions </span>({regions.length})
            </button>
            {isOwnerOrAdmin && (otherTaggings.data?.length ?? 0) > 0 && (
              <button
                className="btn-ghost"
                onClick={() => setShowOthers((v) => !v)}
                title={showOthers ? "Hide other reviewers' taggings" : "Show other reviewers' taggings"}
              >
                {showOthers ? <EyeOff size={16} /> : <Eye size={16} />}
                <span className="hidden sm:inline">Others ({otherTaggings.data?.length ?? 0})</span>
              </button>
            )}
            <button
              className="btn-ghost"
              disabled={taskHistory.length === 0}
              onClick={() => {
                // Back to the previously-viewed file (the one before Next/Submit).
                const prev = taskHistory[taskHistory.length - 1];
                if (prev == null) return;
                setTaskHistory((h) => h.slice(0, -1));
                navigate(`/projects/${pid}/tasks/${prev}`);
              }}
              title={taskHistory.length === 0 ? "No previous file" : "Back to previous file"}
            >
              <ChevronLeft size={16} /> <span className="hidden sm:inline">Back</span>
            </button>
            <button
              className="btn-ghost"
              onClick={async () => {
                // Advance PAST the current task — without `after`, the server
                // returns the lowest pending task, which is the one we're on
                // (still pending until submitted), so Next would do nothing.
                const next = await TasksApi.next(pid, tid).catch(() => null);
                if (next && next.id !== tid) {
                  setTaskHistory((h) => [...h, tid]);
                  navigate(`/projects/${pid}/tasks/${next.id}`);
                }
              }}
              title="Next task"
            >
              <span className="hidden sm:inline">Next </span><ChevronRight size={16} />
            </button>
            <button
              className="btn-ghost"
              onClick={resetToServer}
              disabled={!dirty || save.isPending}
              title="Discard local changes"
            >
              <RotateCcw size={16} /> <span className="hidden sm:inline">Reset</span>
            </button>
            <button
              className="btn-ghost"
              onClick={() => save.mutate(false)}
              disabled={!dirty || save.isPending}
              title="Save draft"
            >
              <Save size={16} /> <span className="hidden sm:inline">Save draft</span>
            </button>
            <button
              className="btn-primary"
              onClick={() => save.mutate(true)}
              disabled={save.isPending}
            >
              <Send size={16} /> Submit
            </button>
          </>
        }
      />
      </div>

      <div className="relative flex flex-col gap-2 px-3 py-2 md:grid md:h-[calc(100vh-93px)] md:grid-cols-1 md:gap-4 md:px-8 md:py-6">
        <div className="flex min-h-0 flex-1 flex-col gap-2 md:gap-3">
          {/* Tag selector */}
          <div className="card flex items-center gap-2 px-4 py-3">
            <div className="text-xs font-medium uppercase tracking-wider text-text-dim">Tags</div>
            <div className="flex flex-wrap gap-2">
              {project.data?.tag_schema?.map((tag) => (
                <button
                  key={tag.name}
                  type="button"
                  onClick={() => setActiveLabel(tag.name)}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
                    activeLabel === tag.name
                      ? "border-accent bg-bg-elevated text-text"
                      : "border-border bg-bg-surface text-text-muted hover:bg-bg-elevated",
                  )}
                >
                  <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                  {tag.shortcut && (
                    <kbd className="ml-1 rounded bg-bg px-1.5 text-[10px] uppercase text-text-dim">
                      {tag.shortcut}
                    </kbd>
                  )}
                </button>
              ))}
            </div>
          </div>

          {wsError && (
            <div className="rounded-md border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {wsError}
            </div>
          )}
          {!wsReady && !wsError && presign.data?.url && (
            <div className="rounded-md border border-border bg-bg-surface px-3 py-2 text-xs text-text-muted">
              Loading audio…
            </div>
          )}

          {/* Display + playback controls on one row. Spectrogram range and
              volume boost both persist per-user in localStorage. Collapsed by
              default on mobile (toggled from the transport bar); always shown at md+. */}
          <div className={cn(
            "card flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2 text-xs text-text-muted md:flex",
            mobileControlsOpen ? "flex" : "hidden",
          )}>
            {/* Spectrogram dynamic range */}
            <span className="font-medium uppercase tracking-wider text-text-dim">Spectrogram</span>
            <label className="flex items-center gap-2">
              <span>Range</span>
              <input
                type="range"
                min={SPEC_RANGE_MIN}
                max={SPEC_RANGE_MAX}
                step={1}
                value={rangeDB}
                onChange={(e) => setRangeDB(Number(e.target.value))}
                className="h-1 w-28 cursor-pointer accent-accent"
                title="Smaller range = more contrast. Larger range = more detail in quiet content."
              />
              <span className="w-12 tabular-nums">{rangeDB} dB</span>
            </label>
            <button
              type="button"
              className="rounded border border-border px-2 py-0.5 hover:bg-bg-elevated"
              onClick={() => setRangeDB(SPEC_RANGE_DEFAULT)}
              title="Reset range to default"
            >
              Reset
            </button>

            {/* Frequency-axis scale: linear (default) | mel. */}
            <label className="flex items-center gap-2">
              <span>Scale</span>
              <div className="flex overflow-hidden rounded-md border border-border" role="group" aria-label="Spectrogram frequency scale">
                <button
                  type="button"
                  onClick={() => setSpecScale("linear")}
                  className={cn(
                    "px-2 py-0.5",
                    specScale === "linear" ? "bg-bg-elevated text-text" : "text-text-muted hover:bg-bg-elevated",
                  )}
                  aria-pressed={specScale === "linear"}
                  title="Linear frequency axis"
                >
                  Linear
                </button>
                <button
                  type="button"
                  onClick={() => setSpecScale("mel")}
                  className={cn(
                    "border-l border-border px-2 py-0.5",
                    specScale === "mel" ? "bg-bg-elevated text-text" : "text-text-muted hover:bg-bg-elevated",
                  )}
                  aria-pressed={specScale === "mel"}
                  title="Mel frequency axis (perceptual; more detail in low frequencies)"
                >
                  Mel
                </button>
              </div>
            </label>

            <span className="h-5 w-px bg-border" aria-hidden />

            {/* Playback volume — files are auto-leveled; this boosts further. */}
            <span className="flex items-center gap-1.5 font-medium uppercase tracking-wider text-text-dim">
              <Volume2 size={14} /> Volume
            </span>
            <label className="flex items-center gap-2">
              <span>Boost</span>
              <input
                type="range"
                min={MANUAL_MIN}
                max={MANUAL_MAX}
                step={0.1}
                value={manualBoost}
                onChange={(e) => setManualBoost(clampManualBoost(Number(e.target.value)))}
                className="h-1 w-28 cursor-pointer accent-accent"
                title="Boost playback volume. Auto-leveling is always applied; this multiplies on top."
                aria-label="Playback volume boost"
                aria-valuetext={`${manualBoost.toFixed(1)} times`}
              />
              <span className="w-10 tabular-nums">{manualBoost.toFixed(1)}×</span>
            </label>
            <span className="text-text-dim">
              = <span className="tabular-nums text-text-muted">{(autoGain * manualBoost).toFixed(1)}×</span>
              <span className="ml-1 text-[10px]">(auto {autoGain.toFixed(1)}×)</span>
            </span>
            <button
              type="button"
              className="rounded border border-border px-2 py-0.5 hover:bg-bg-elevated"
              onClick={() => setManualBoost(MANUAL_DEFAULT)}
              title="Reset boost to 1×"
            >
              Reset
            </button>
          </div>

          {/* Waveform + spectrogram (spectrogram renders inside wavesurfer's container
              so the playhead cursor spans both visualizations).
              flex-1 + min-h-0 → takes all remaining vertical space without overflow. */}
          {/* Mobile: FIXED height (~ what a 14 Pro Max's flex layout yields), so the
              spectrogram is equally big on small phones — the page scrolls instead.
              Desktop: flex-1 fills the fixed-height grid row, unchanged. */}
          <div className="card flex-1 overflow-hidden p-2 min-h-0 max-md:h-[420px] max-md:flex-none md:p-3">
            <div ref={waveformRef} className="h-full w-full" />
          </div>

          {/* Transport — at the bottom, below the spectrogram */}
          <div className="card flex flex-wrap items-center gap-3 px-4 py-2 md:flex-nowrap">
            <button
              type="button"
              className="rounded p-2 hover:bg-bg-elevated max-md:bg-bg-elevated"
              onClick={() => wsRef.current?.playPause()}
              title="Space"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
            {/* Keyboard hints — desktop only. */}
            <div className="hidden items-center gap-3 text-xs text-text-muted md:flex">
              <Shortcut keys={["Space"]} desc="Play / pause" />
              <Shortcut
                keys={["←", "→"]}
                desc="Step playhead ±1s"
                extra="Hold Shift for ±5s"
              />
              <Shortcut
                keys={["Z"]}
                desc="Start / end a region at the playhead"
                extra="Esc cancels mid-selection"
              />
              <Shortcut keys={["X"]} desc="Cycle the active tag" />
            </div>
            {selectionStartTime !== null && (
              <span className="hidden rounded-md border border-status-warn/40 bg-status-warn/10 px-2 py-1 text-xs text-status-warn md:inline-flex">
                Range started at {selectionStartTime.toFixed(2)}s — press <kbd className="rounded bg-bg px-1 text-[10px]">Z</kbd> again to close, <kbd className="rounded bg-bg px-1 text-[10px]">Esc</kbd> to cancel
              </span>
            )}
            <div className="ml-auto flex items-center gap-2 text-text-muted">
              {/* Mobile: toggle the collapsed spectrogram/volume controls. */}
              <button
                type="button"
                className={cn(
                  "rounded p-2 hover:bg-bg-elevated md:hidden",
                  mobileControlsOpen && "bg-bg-elevated text-text",
                )}
                onClick={() => setMobileControlsOpen((v) => !v)}
                title="Display & volume settings"
                aria-label="Display and volume settings"
                aria-pressed={mobileControlsOpen}
              >
                <SlidersHorizontal size={16} />
              </button>
              <button
                type="button"
                className="rounded p-1.5 hover:bg-bg-elevated max-md:p-2"
                onClick={() => setPxPerSec((v) => Math.max(10, Math.round(v / 1.5)))}
                title="Zoom out"
                aria-label="Zoom out"
              >
                <ZoomOut size={16} />
              </button>
              <span className="text-xs">{pxPerSec} px/s</span>
              <button
                type="button"
                className="rounded p-1.5 hover:bg-bg-elevated max-md:p-2"
                onClick={() => setPxPerSec((v) => Math.min(1000, Math.round(v * 1.5)))}
                title="Zoom in"
                aria-label="Zoom in"
              >
                <ZoomIn size={16} />
              </button>
            </div>
          </div>

          {/* Mobile touch controls — replacements for the keyboard shortcuts.
              Hidden on desktop (md+), where the keyboard is used instead. */}
          <div className="card flex flex-col gap-2 p-3 md:hidden">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-text-dim">Playhead</span>
              <div className="flex items-center gap-1.5">
                <button type="button" className="btn-ghost px-2.5 py-2 text-xs" onClick={() => nudgePlayhead(-5)} aria-label="Back 5 seconds">−5s</button>
                <button type="button" className="btn-ghost px-2.5 py-2 text-xs" onClick={() => nudgePlayhead(-1)} aria-label="Back 1 second">−1s</button>
                <button type="button" className="btn-ghost px-2.5 py-2 text-xs" onClick={() => nudgePlayhead(1)} aria-label="Forward 1 second">+1s</button>
                <button type="button" className="btn-ghost px-2.5 py-2 text-xs" onClick={() => nudgePlayhead(5)} aria-label="Forward 5 seconds">+5s</button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-primary flex-1 py-2.5"
                onClick={markRegionPoint}
              >
                {selectionStartTime === null ? "Set region start" : "Set region end"}
              </button>
              {selectionStartTime !== null && (
                <button type="button" className="btn-ghost py-2.5" onClick={cancelSelection}>
                  Cancel
                </button>
              )}
            </div>
            {selectionStartTime !== null ? (
              <p className="text-xs text-status-warn">
                Start at {selectionStartTime.toFixed(2)}s — move the playhead, then tap “Set region end”. New regions use the “{activeLabel}” tag.
              </p>
            ) : (
              <p className="text-[11px] text-text-dim">
                Tap the waveform to seek · drag to pan · use the zoom buttons above to zoom.
              </p>
            )}
          </div>
        </div>

        {/* Region panel — a right-side overlay on desktop, a bottom sheet on mobile. */}
        {regionsPanelOpen && (
        <div className="card absolute z-20 flex flex-col overflow-hidden shadow-xl max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:max-h-[65vh] max-md:rounded-b-none md:right-8 md:top-6 md:bottom-6 md:w-80">
          <div className="flex items-center justify-between border-b border-border-muted px-4 py-3 text-xs font-semibold uppercase tracking-wider text-text-dim">
            <span>Regions ({regions.length})</span>
            <button
              type="button"
              onClick={() => setRegionsPanelOpen(false)}
              className="rounded p-1 text-text-dim hover:bg-bg-elevated hover:text-text max-md:p-2"
              title="Hide panel"
              aria-label="Hide regions panel"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {regions.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-text-muted">
                No regions yet. <span className="md:hidden">Tap to seek, drag to pan — use “Set region start” below to mark a region.</span><span className="hidden md:inline">Drag on the waveform to create one.</span>
              </div>
            ) : (
              regions
                .slice()
                .sort((a, b) => a.start - b.start)
                .map((r) => (
                  <div
                    key={r.uid}
                    className="border-b border-border-muted px-4 py-2.5 hover:bg-bg-elevated"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colorFor(r.label) }} />
                      <button
                        type="button"
                        onClick={() => wsRef.current?.setTime(r.start)}
                        className="flex-1 text-left"
                      >
                        <div className="text-sm font-medium">{r.label}</div>
                        <div className="font-mono text-[11px] text-text-dim">
                          {r.start.toFixed(2)}s → {r.end.toFixed(2)}s ({(r.end - r.start).toFixed(2)}s)
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRegion(r.uid)}
                        className="rounded p-1 text-text-dim hover:bg-bg hover:text-status-error max-md:p-2"
                        aria-label="Delete region"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    {/* Mobile fine-tune for region bounds (drag/resize is imprecise on touch). */}
                    <div className="mt-2 flex items-center gap-1 text-[11px] text-text-dim md:hidden">
                      <span className="w-9">start</span>
                      <button type="button" className="btn-ghost px-2 py-1" onClick={() => nudgeRegionBound(r.uid, "start", -0.1)} aria-label="Start earlier">−</button>
                      <button type="button" className="btn-ghost px-2 py-1" onClick={() => nudgeRegionBound(r.uid, "start", 0.1)} aria-label="Start later">+</button>
                      <span className="ml-2 w-7">end</span>
                      <button type="button" className="btn-ghost px-2 py-1" onClick={() => nudgeRegionBound(r.uid, "end", -0.1)} aria-label="End earlier">−</button>
                      <button type="button" className="btn-ghost px-2 py-1" onClick={() => nudgeRegionBound(r.uid, "end", 0.1)} aria-label="End later">+</button>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compact shortcut chip: one or more `<kbd>` keys + a short description, with
 * an optional `extra` line shown in the tooltip on hover (for modifier behavior).
 */
function Shortcut({
  keys,
  desc,
  extra,
}: {
  keys: string[];
  desc: string;
  extra?: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap"
      title={extra ? `${desc}\n${extra}` : desc}
    >
      <span className="flex items-center gap-0.5">
        {keys.map((k) => (
          <kbd
            key={k}
            className="rounded border border-border-muted bg-bg px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-text"
          >
            {k}
          </kbd>
        ))}
      </span>
      <span className="text-text-dim">{desc}</span>
    </span>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return `rgba(34, 211, 238, ${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}, ${alpha})`;
}

/**
 * Build the in-region label content with an inline × button so the user can
 * delete a region directly on the waveform — no need to open the Regions panel.
 * stopPropagation on mousedown/click is critical: without it, the click bubbles
 * to wavesurfer and triggers seek/drag selection.
 */
function buildRegionContent(label: string, onDelete: () => void): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "ws-region-content";
  wrap.style.cssText = [
    "display: inline-flex",
    "align-items: center",
    "gap: 4px",
    "padding: 1px 4px 1px 6px",
    "font-size: 11px",
    "font-weight: 600",
    "color: #fff",
    "background: rgba(0,0,0,0.55)",
    "border-radius: 3px",
    "pointer-events: auto",
  ].join("; ");

  const text = document.createElement("span");
  text.textContent = label;
  wrap.appendChild(text);

  const close = document.createElement("button");
  close.type = "button";
  close.setAttribute("aria-label", "delete region");
  close.title = "Delete region";
  close.textContent = "×";
  close.style.cssText = [
    "background: transparent",
    "border: 0",
    "color: #fff",
    "cursor: pointer",
    "font-size: 14px",
    "line-height: 1",
    "padding: 0 2px",
    "opacity: 0.7",
  ].join("; ");
  close.addEventListener("mouseenter", () => (close.style.opacity = "1"));
  close.addEventListener("mouseleave", () => (close.style.opacity = "0.7"));
  // Critical: stop wavesurfer from picking up the click as a seek/drag.
  close.addEventListener("mousedown", (e) => e.stopPropagation());
  close.addEventListener("click", (e) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete();
  });
  wrap.appendChild(close);
  return wrap;
}

/**
 * Read-only label content for "others' taggings" overlay regions. No × button —
 * the user can't delete other reviewers' work. Shows `username · label` in the
 * reviewer's overlay color so adjacent overlapping submissions are visually
 * distinguishable.
 */
function buildOverlayRegionContent(username: string, label: string, color: string): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "ws-region-content";
  wrap.style.cssText = [
    "display: inline-flex",
    "align-items: center",
    "gap: 4px",
    "padding: 1px 6px",
    "font-size: 11px",
    "font-weight: 600",
    "color: #fff",
    `background: ${color}`,
    "border-radius: 3px",
    "pointer-events: none",
    "opacity: 0.85",
  ].join("; ");
  wrap.textContent = `${username} · ${label}`;
  return wrap;
}
